import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import anime from 'animejs';
import styled from 'styled-components';
import { IconLoader } from '@components/icons';

// Stage durations in milliseconds (1000 = one second).
const LOADER_TIMING = {
  unlit: 350, // Show the dark glass before ignition.
  flicker: 460, // Entire ignition sequence, until the whole sign is fully lit.
  litHold: 350, // Hold at full brightness.
  lift: 620, // Pull the sign above the screen.
  fade: 450, // Reveal the homepage, starting halfway through the lift.
};

const StyledLoader = styled.div`
  /* Surrounding glow strength: 0 = none, 1 = full. Keeps tube color unchanged. */
  --neon-glow-strength: 0.86;

  ${({ theme }) => theme.mixins.flexCenter};
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: var(--loader-background);
  z-index: 99;
  overflow: hidden;

  .logo-wrapper {
    position: relative;
    width: 230px;
    max-width: 65vw;

    .suspension-wire {
      position: absolute;
      width: 1px;
      height: 100vh;
      background: linear-gradient(to bottom, var(--neon-wire-dark) 45%, var(--neon-wire-light));
      opacity: 0.65;
      pointer-events: none;

      &::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: -2px;
        width: 5px;
        height: 5px;
        border: 1px solid var(--neon-wire-ring);
        border-radius: 50%;
      }
    }

    /* Attachment points in the lettering's -2 0 138 94 viewBox. */
    .suspension-wire-f {
      left: 36.9565%;
      bottom: 80.8511%;
    }

    .suspension-wire-g {
      left: 74.6377%;
      bottom: 85.1064%;
    }

    svg {
      position: relative;
      display: block;
      width: 100%;
      height: auto;
      margin: 0 auto;
      user-select: none;
    }

    svg {
      overflow: visible;
    }

    .neon-letter path {
      fill: none;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .neon-tube {
      stroke: url(#neon-glass);
      stroke-width: 3.6;
      filter: url(#neon-grain);
    }

    .neon-glass-edge {
      fill: none;
      stroke: var(--neon-glass-edge);
      stroke-width: 0.6;
      opacity: 0.4;
      transform: translate(-0.65px, -0.65px);
    }

    .neon-light {
      opacity: 0;
    }

    .neon-halo {
      stroke: var(--green);
      stroke-width: 5.2;
      filter: url(#neon-bloom);
      opacity: var(--neon-glow-strength);
    }

    .neon-gas {
      stroke: url(#neon-gas);
      stroke-width: 3.2;
      filter: url(#neon-grain);
    }

    .neon-core {
      stroke: var(--neon-core);
      stroke-width: 1.15;
      filter: url(#neon-filament);
      opacity: 1;
    }

    .neon-clips {
      stroke: var(--neon-clips);
      stroke-width: 1;
      opacity: 0.75;
    }

    .neon-spark {
      opacity: 0;
      stroke: var(--neon-core);
      stroke-width: 0.5;
      stroke-linecap: round;
      filter: drop-shadow(0 0 1px var(--green));
    }
  }
`;

const Loader = ({ finishLoading }) => {
  const container = useRef(null);

  useEffect(() => {
    const element = container.current;
    const logo = element.querySelector('.logo-wrapper');
    const lights = Array.from(element.querySelectorAll('.neon-light'));
    const sparks = Array.from(element.querySelectorAll('.neon-spark'));
    const animation = anime.timeline({ autoplay: false, complete: finishLoading });
    const ignitionScale = LOADER_TIMING.flicker / 1090;

    // The unlit tubes stay visible while each letter's power catches independently.
    lights.forEach((light, index) => {
      animation.add(
        {
          targets: light,
          // Scale the gaps between pulses, keeping the actual ignition/decay very brief.
          opacity: [
            { value: 0.55, delay: 13, duration: 12 },
            { value: 0.02, delay: 67, duration: 18 },
            { value: 0.85, delay: 8, duration: 12 },
            { value: 0.08, delay: 122, duration: 18 },
            { value: 0.45, delay: 23, duration: 12 },
            { value: 0.2, delay: 77, duration: 18 },
            { value: 1, delay: 18, duration: 12 },
            { value: 0.82, delay: 172, duration: 18 },
            { value: 1, delay: 218, duration: 12 },
          ].map(({ value, delay, duration }) => {
            const slot = (delay + duration) * ignitionScale;
            const transition = Math.min(duration, slot);
            return { value, delay: slot - transition, duration: transition };
          }),
          easing: 'easeOutQuad',
        },
        LOADER_TIMING.unlit + (index / Math.max(1, lights.length - 1)) * 240 * ignitionScale,
      );
    });
    sparks.forEach((spark, index) => {
      const direction = index % 5;
      animation.add(
        {
          targets: spark,
          duration: 355 * ignitionScale,
          opacity: [
            { value: 1, duration: 15 * ignitionScale },
            { value: 0, duration: 340 * ignitionScale },
          ],
          translateX: [0, [-12, -5, 7, 15, 20][direction]],
          translateY: [
            { value: [-6, -12, -9, -3, 3][direction], duration: 120 * ignitionScale },
            { value: [7, 2, 5, 12, 19][direction], duration: 235 * ignitionScale },
          ],
          easing: 'easeOutQuad',
        },
        LOADER_TIMING.unlit +
          (130 + Number(spark.dataset.burst) * 390 + direction * 12) * ignitionScale,
      );
    });
    // The lit sign and its suspension wires are hoisted together above the viewport.
    const liftStart = LOADER_TIMING.unlit + LOADER_TIMING.flicker + LOADER_TIMING.litHold;
    animation.add(
      {
        targets: logo,
        translateY: [0, '-100vh'],
        duration: LOADER_TIMING.lift,
        easing: 'easeInCubic',
      },
      liftStart,
    );
    // Reveal the already-mounted homepage as the sign clears the center of the screen.
    animation.add(
      {
        targets: element,
        duration: LOADER_TIMING.fade,
        opacity: [1, 0],
        easing: 'easeInOutSine',
      },
      liftStart + LOADER_TIMING.lift / 2,
    );

    animation.play();

    return () => {
      animation.pause();
      anime.remove(lights);
      anime.remove(sparks);
      anime.remove(logo);
      anime.remove(element);
    };
  }, []);

  return (
    <StyledLoader className="loader" ref={container}>
      <Helmet bodyAttributes={{ class: 'hidden' }} />
      <div className="logo-wrapper">
        <span className="suspension-wire suspension-wire-f" aria-hidden="true" />
        <span className="suspension-wire suspension-wire-g" aria-hidden="true" />
        <IconLoader />
      </div>
    </StyledLoader>
  );
};

Loader.propTypes = {
  finishLoading: PropTypes.func.isRequired,
};

export default Loader;
